/** Candidate types (RFC 8445): direct, via STUN-discovered address, peer-reflexive, or TURN. */
export type CandidateType = "host" | "srflx" | "prflx" | "relay";

export interface ConnectionStats {
  /** Direct WebRTC (possibly through TURN), or a QUIC connection through an iroh relay. */
  transport?: "webrtc" | "iroh";
  /** The relay carrying an iroh connection. */
  relayUrl?: string;
  /** The candidate pair in use, once ICE picked one. */
  pair?: {
    local: { type?: CandidateType; protocol?: string; relayProtocol?: string };
    remote: { type?: CandidateType; protocol?: string };
    /** Seconds (ICE consent checks). */
    rtt?: number;
    bytesSent?: number;
    bytesReceived?: number;
  };
  channels: {
    label: string;
    protocol: string;
    state: string;
    messagesSent: number;
    messagesReceived: number;
    bytesSent: number;
    bytesReceived: number;
  }[];
}

type Entry = Record<string, unknown> & { id: string; type: string };
const num = (v: unknown) => (typeof v === "number" ? v : undefined);
const str = (v: unknown) => (typeof v === "string" ? v : undefined);

/**
 * The useful part of a getStats() report. Deliberately leaves out addresses and ports: this
 * ends up in shareable diagnostics.
 */
export function summarizeStats(
  report: Iterable<[string, unknown]> | Map<string, unknown>,
): ConnectionStats {
  const byId = new Map<string, Entry>();
  for (const [id, value] of report) {
    if (value && typeof value === "object") byId.set(id, { ...(value as object), id } as Entry);
  }
  const entries = [...byId.values()];
  // Chromium names the pair on the transport; Firefox flags it as `selected`.
  const transport = entries.find((e) => e.type === "transport" && e.selectedCandidatePairId);
  const pair =
    (transport ? byId.get(String(transport.selectedCandidatePairId)) : undefined) ??
    entries.find((e) => e.type === "candidate-pair" && e.selected === true) ??
    entries.find(
      (e) => e.type === "candidate-pair" && e.nominated === true && e.state === "succeeded",
    );
  const candidate = (id: unknown) => (typeof id === "string" ? byId.get(id) : undefined);

  const stats: ConnectionStats = {
    transport: "webrtc",
    channels: entries
      .filter((e) => e.type === "data-channel")
      .map((e) => ({
        label: str(e.label) ?? "",
        protocol: str(e.protocol) ?? "",
        state: str(e.state) ?? "",
        messagesSent: num(e.messagesSent) ?? 0,
        messagesReceived: num(e.messagesReceived) ?? 0,
        bytesSent: num(e.bytesSent) ?? 0,
        bytesReceived: num(e.bytesReceived) ?? 0,
      })),
  };
  if (pair) {
    const local = candidate(pair.localCandidateId);
    const remote = candidate(pair.remoteCandidateId);
    stats.pair = {
      local: {
        type: str(local?.candidateType) as CandidateType | undefined,
        protocol: str(local?.protocol),
        relayProtocol: str(local?.relayProtocol),
      },
      remote: {
        type: str(remote?.candidateType) as CandidateType | undefined,
        protocol: str(remote?.protocol),
      },
      rtt: num(pair.currentRoundTripTime),
      bytesSent: num(pair.bytesSent),
      bytesReceived: num(pair.bytesReceived),
    };
  }
  return stats;
}
